import { ReactNode, useEffect, Suspense } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { SkipLinks } from "@/components/ui/SkipLinks";
import { prefetchProfileDashboardData } from "@/hooks/useProfileRoutePrefetch";
import { DashboardSidebarLayout } from "@/components/layout/DashboardSidebarLayout";
import { UserSidebar } from "@/components/layout/UserSidebar";
import { useAuthReady } from "@/hooks/useAuthReady";
import { PageLoader } from "@/components/ui/PageLoader";
import { useAuth } from "@/supabase-auth";

interface UserLayoutProps {
  children: ReactNode;
}

export function UserLayout({ children }: UserLayoutProps) {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const { isReady, hasAccessToken } = useAuthReady();
  const userId = session?.user?.id;

  // CRITICAL: Wait for auth hydration before prefetching
  useEffect(() => {
    if (!isReady || !hasAccessToken || !userId) return;

    void prefetchProfileDashboardData(queryClient, userId);
  }, [isReady, hasAccessToken, userId, queryClient]);

  // CRITICAL: Don't render children until auth is ready
  if (!isReady) {
    return <PageLoader />;
  }

  return (
    <div className="min-h-screen bg-background">
      <SkipLinks />
      <DashboardSidebarLayout 
        sidebar={<UserSidebar />}
        headerContext="user"
      >
        <Suspense fallback={<PageLoader />}>
          {children}
        </Suspense>
      </DashboardSidebarLayout>
    </div>
  );
}
